import React, { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useStrings } from '@/hooks/useStrings';
import { safeWrite } from '@/utils/supabaseWrite';

const s = {
  screen: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'var(--bg)',
    display: 'flex',
    flexDirection: 'column',
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '28px 22px 18px',
  },
  header: {
    textAlign: 'center',
    marginBottom: 18,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  logo: {
    width: 96,
    height: 'auto',
    display: 'block',
  },
  h1: {
    fontFamily: 'var(--font-title)',
    fontSize: 36,
    fontWeight: 500,
    fontStyle: 'italic',
    color: 'var(--dark-brown)',
    textAlign: 'center',
    lineHeight: 1.15,
    letterSpacing: '-0.015em',
    margin: '8px 0 22px',
    fontVariationSettings: '"opsz" 96',
  },
  accent: {
    color: 'var(--primary)',
    fontStyle: 'italic',
  },
  intro: {
    fontFamily: 'var(--font-body)',
    fontSize: 19,
    fontWeight: 400,
    color: 'var(--text)',
    lineHeight: 1.7,
    marginBottom: 24,
    textAlign: 'left',
  },
  card: {
    background: 'var(--surface)',
    borderRadius: 18,
    padding: '22px 20px 20px',
    marginBottom: 16,
    border: '1px solid var(--border)',
    boxShadow: '0 4px 18px rgba(45, 26, 31, 0.04)',
  },
  cardLabel: {
    fontFamily: 'var(--font-ui)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--primary)',
    marginBottom: 14,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
  },
  cardLabelDot: {
    display: 'inline-block',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--primary)',
  },
  bullet: {
    fontFamily: 'var(--font-body)',
    fontSize: 17,
    fontWeight: 400,
    lineHeight: 1.6,
    color: 'var(--text)',
    paddingLeft: 22,
    position: 'relative',
    marginBottom: 12,
  },
  bulletDot: {
    position: 'absolute',
    left: 4,
    top: 8,
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--primary)',
  },
  noteBox: {
    fontFamily: 'var(--font-body)',
    marginTop: 14,
    paddingTop: 14,
    borderTop: '1px solid var(--border)',
    fontSize: 16,
    fontStyle: 'italic',
    color: 'var(--warm-brown)',
    lineHeight: 1.65,
  },
  disclaimerText: {
    fontFamily: 'var(--font-body)',
    fontSize: 17,
    fontWeight: 400,
    color: 'var(--text)',
    lineHeight: 1.65,
    marginBottom: 10,
  },
  footer: {
    flexShrink: 0,
    background:
      'linear-gradient(180deg, rgba(242, 240, 245, 0) 0%, var(--bg) 24%)',
    paddingTop: 16,
    paddingLeft: 22,
    paddingRight: 22,
    paddingBottom: 'calc(18px + env(safe-area-inset-bottom, 0px))',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 16,
    padding: '14px 16px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    cursor: 'pointer',
    transition: 'border-color 180ms ease, background 180ms ease',
  },
  checkRowChecked: {
    borderColor: 'var(--primary)',
    background: 'var(--primary-pale)',
  },
  // Visually-hidden native checkbox — keeps keyboard + screen reader
  // behavior and remains clickable via the wrapping <label>. The
  // visible state is the .checkboxBox div next to it.
  checkboxNative: {
    position: 'absolute',
    width: 1,
    height: 1,
    opacity: 0,
  },
  checkboxBox: {
    width: 26,
    height: 26,
    minWidth: 26,
    minHeight: 26,
    borderRadius: 6,
    border: '2px solid var(--primary, #A8003F)',
    background: '#fff',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 150ms ease, border-color 150ms ease',
  },
  checkboxBoxChecked: {
    background: 'var(--primary, #A8003F)',
    borderColor: 'var(--primary, #A8003F)',
  },
  checkboxTick: {
    color: '#fff',
    fontSize: 16,
    lineHeight: 1,
    fontWeight: 700,
  },
  checkLabel: {
    fontFamily: "var(--font-ui, 'DM Sans', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1.5,
    color: 'var(--text, #3D2B1F)',
    cursor: 'pointer',
    flex: 1,
  },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    padding: '18px 24px',
    minHeight: 56,
    border: 'none',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    textAlign: 'center',
    transition:
      'background 220ms ease, opacity 220ms ease, transform 120ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 220ms ease',
    touchAction: 'manipulation',
    fontFamily: 'var(--font-ui)',
    WebkitTapHighlightColor: 'transparent',
  },
  ctaArrow: {
    fontSize: 15,
    display: 'inline-block',
  },
};

export default function ConsentMessage({ onComplete }) {
  const { user } = useApp();
  const t = useStrings();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  const canSubmit = checked && !saving;

  async function handleConsent() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.consent_complete = true;
      // Fire-and-forget. safeWrite writes bb7 to localStorage
      // synchronously (before its first await), so the local source of
      // truth is persisted before this call returns a pending promise.
      // The Supabase round-trip is a best-effort mirror; if it is slow
      // or fails, the background promise settles with a console.error
      // and the user has already been advanced past the consent gate.
      // Matches the LanguageSelector fix — see src/App.jsx AppShell.
      safeWrite(user?.id, data).catch((err) => {
        console.error('ConsentMessage background save failed:', err);
      });
      onComplete();
    } catch (err) {
      console.error('Consent save failed:', err);
      setSaving(false);
    }
  }

  const ctaStyle = canSubmit
    ? {
        ...s.cta,
        background: 'var(--primary)',
        color: '#fff',
        cursor: 'pointer',
        opacity: 1,
        boxShadow:
          '0 10px 24px -10px rgba(168, 0, 63, 0.35), inset 0 1px 0 rgba(255,255,255,0.22)',
      }
    : {
        ...s.cta,
        background: 'var(--surface-high, #E8DDD0)',
        color: 'var(--text-secondary, #6B5A4A)',
        cursor: 'not-allowed',
        opacity: 0.55,
      };

  const checkRowStyle = {
    ...s.checkRow,
    ...(checked ? s.checkRowChecked : null),
  };

  return (
    <div style={s.screen}>
      <div style={s.scroll}>
        <div
          className="motion-rise"
          style={{ ...s.header, '--stagger-i': 0 }}
        >
          <img
            src="/logo.png"
            alt="Be Bold"
            width="96"
            height="96"
            style={s.logo}
          />
        </div>

        <h1
          className="motion-rise"
          style={{ ...s.h1, '--stagger-i': 1 }}
        >
          <span style={s.accent}>3D</span> {t('consent_method_name')}
        </h1>

        <p
          className="motion-rise"
          style={{ ...s.intro, '--stagger-i': 2 }}
        >
          {t('consent_body')}
        </p>

        {/* Safety info */}
        <div
          className="motion-rise"
          style={{ ...s.card, '--stagger-i': 3 }}
        >
          <div style={s.cardLabel}>
            <span aria-hidden="true" style={s.cardLabelDot} />
            {t('consent_safety_title')}
          </div>
          {['consent_pregnant', 'consent_postpartum', 'consent_pain', 'consent_surgery'].map((key) => (
            <div key={key} style={s.bullet}>
              <span aria-hidden="true" style={s.bulletDot} />
              {t(key)}
            </div>
          ))}
          <div style={s.noteBox}>{t('consent_relax_note')}</div>
        </div>

        {/* Disclaimer */}
        <div
          className="motion-rise"
          style={{ ...s.card, '--stagger-i': 4 }}
        >
          <div style={s.cardLabel}>
            <span aria-hidden="true" style={s.cardLabelDot} />
            {t('consent_disclaimer_title')}
          </div>
          <p style={s.disclaimerText}>{t('consent_responsibility')}</p>
          <p style={{ ...s.disclaimerText, marginBottom: 0 }}>{t('consent_liability')}</p>
        </div>

        {/* Data-handling note — short, first-person, appears above
            the unified footer so the user sees the privacy stance
            before she agrees to anything. Deliberately light on
            legalese; the full privacy policy is a tap away. */}
        <div
          className="motion-rise"
          style={{ ...s.card, '--stagger-i': 5 }}
        >
          <div style={s.cardLabel}>
            <span aria-hidden="true" style={s.cardLabelDot} />
            {t('consent_data_title')}
          </div>
          <p style={s.disclaimerText}>{t('consent_data_body')}</p>
        </div>
      </div>

      {/* Unified footer: checkbox + CTA in one container */}
      <div style={s.footer}>
        {/* The entire row is a <label> so tapping anywhere — box or text —
            toggles the hidden native checkbox. The visible state is the
            rose-bordered box, which fills with primary + a white tick
            when checked. Native input stays in the DOM (visually hidden)
            for keyboard focus + screen-reader semantics. */}
        <label htmlFor="consent-check" style={checkRowStyle}>
          <input
            type="checkbox"
            id="consent-check"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={s.checkboxNative}
          />
          <span
            aria-hidden="true"
            style={{
              ...s.checkboxBox,
              ...(checked ? s.checkboxBoxChecked : null),
            }}
          >
            {checked && <span className="motion-pop" style={s.checkboxTick}>{'\u2713'}</span>}
          </span>
          <span style={s.checkLabel}>
            {t('consent_checkbox')}
          </span>
        </label>
        <button
          type="button" style={ctaStyle}
          onClick={handleConsent}
          disabled={!canSubmit}
          className={canSubmit ? 'motion-breath' : undefined}
        >
          {t('consent_continue')}
          <span aria-hidden="true" style={s.ctaArrow}>{'→'}</span>
        </button>
      </div>
    </div>
  );
}
